import React, { useCallback, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faGraduationCap, faQrcode } from '@fortawesome/free-solid-svg-icons';
import PixPayment from '../payment/PixPayment';
import CardPayment from '../payment/CardPayment';
import OrderSummary from '../payment/OrderSummary';
import CertificateOffer from './CertificateOffer';
import { API_BASE_URL } from '../../config/api';
import '../../styles/Carrinho.css';

const PURCHASE_CONFIRMATION_DELAY = 5000;

const Carrinho = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [curso, setCurso] = useState({});
  const [certificatePrice, setCertificatePrice] = useState(29.90);
  const [paymentConfig, setPaymentConfig] = useState({
    mercado_pago_public_key: '',
    card_enabled: false,
    max_installments: 6,
    fourth_certificate_discount: false,
  });
  const [step, setStep] = useState(1);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [cardFormMounted, setCardFormMounted] = useState(false);
  const [switchingPaymentMethod, setSwitchingPaymentMethod] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [lockedFields, setLockedFields] = useState({ email: false, rg: false, cpf: false });

  const [formData, setFormData] = useState({
    country: '',
    fullName: '',
    rg: '',
    cpf: '',
    email: ''
  });
  const [error, setError] = useState('');
  const [purchaseConfirmation, setPurchaseConfirmation] = useState(null);
  const [hasResumeAccess, setHasResumeAccess] = useState(null);
  const includesResume = hasResumeAccess === false;
  const totalPrice = certificatePrice;

  const handlePaymentSuccess = useCallback((paymentData, options = {}) => {
    const resumePurchased = paymentData?.includes_resume ?? includesResume;
    setError('');
    setPurchaseConfirmation({
      destination: resumePurchased ? '/curriculo' : '/meus-certificados',
      replace: Boolean(options.replace),
      resumePurchased,
      destinationMessage: resumePurchased
        ? 'Pagamento aprovado! Certificado e currículo liberados.'
        : 'Pagamento aprovado! Seu Certificado foi liberado.',
    });
  }, [includesResume]);

  const continueAfterPurchase = useCallback(() => {
    if (!purchaseConfirmation) return;
    navigate(purchaseConfirmation.destination, {
      replace: purchaseConfirmation.replace,
      state: {
        message: purchaseConfirmation.destinationMessage,
        type: 'success',
      },
    });
  }, [navigate, purchaseConfirmation]);

  useEffect(() => {
    if (!purchaseConfirmation) return undefined;
    const timeout = window.setTimeout(continueAfterPurchase, PURCHASE_CONFIRMATION_DELAY);
    return () => window.clearTimeout(timeout);
  }, [continueAfterPurchase, purchaseConfirmation]);

  useEffect(() => {
    const returnParams = new URLSearchParams(location.search);
    if (returnParams.get('checkout_return') !== '1') return;

    const externalPaymentId = returnParams.get('payment_id');
    setShowPayment(true);
    setPaymentMethod('credit_card');
    setCardFormMounted(true);
    if (!externalPaymentId) {
      setError('Pagamento não concluído. Você pode tentar novamente.');
      return;
    }

    const syncCheckout = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(`${API_BASE_URL}/payment/card/checkout/sync`, {
          external_payment_id: Number(externalPaymentId),
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.status === 'completed') {
          handlePaymentSuccess(response.data, { replace: true });
          return;
        }

        setError(
          response.data.status === 'pending'
            ? 'Pagamento em análise pelo Mercado Pago.'
            : 'Pagamento não aprovado. Escolha outra forma de pagamento.',
        );
      } catch (requestError) {
        setError(requestError.response?.data?.error || 'Não foi possível confirmar o pagamento.');
      }
    };

    syncCheckout();
  }, [handlePaymentSuccess, location.search]);

  useEffect(() => {
    // Buscar configuração de preço
    const fetchPaymentConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/config/payment`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const configuredPrice = Number(
          response.data.professional_kit_price ?? response.data.certificate_price,
        );
        if (Number.isFinite(configuredPrice) && configuredPrice > 0) {
          setCertificatePrice(configuredPrice);
        }
        setPaymentConfig(response.data);
      } catch (error) {

        // Mantém o valor padrão se houver erro
      }
    };

    // Buscar dados do usuário logado
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login', { replace: true, state: { from: location } });
          return;
        }
        const headers = { 'Authorization': `Bearer ${token}` };
        const [userResponse, accessResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/user`, { headers }),
          axios.get(`${API_BASE_URL}/resumes/access`, { headers }),
        ]);
        setFormData({
          country: userResponse.data.country || 'Brasil',
          fullName: userResponse.data.name || '',
          rg: userResponse.data.rg || '',
          cpf: userResponse.data.cpf || '',
          email: userResponse.data.email || ''
        });
        setLockedFields({
          email: !!userResponse.data.email,
          rg: !!userResponse.data.rg,
          cpf: !!userResponse.data.cpf,
        });
        setHasResumeAccess(Boolean(accessResponse.data.has_access));
      } catch (error) {
        if (error.response?.status === 401) {
          navigate('/login', { replace: true, state: { from: location } });
          return;
        }
        setError('Não foi possível carregar os dados da sua conta. Recarregue a página para tentar novamente.');
      }
    };

    fetchPaymentConfig();
    fetchUserData();

    // Buscar informações do curso
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get('curso_id');
    if (id) {
      const fetchCurso = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/cursos/${id}`);
          setCurso(response.data);
        } catch (error) {

        }
      };

      fetchCurso();
    }
  }, [location, navigate]);

  const handleNextStep = async () => {
    if (step === 2) {
      if (!formData.country || !formData.fullName || !formData.rg || !formData.cpf || !formData.email) {
        setError('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      const token = localStorage.getItem('token');
      try {
        setSavingProfile(true);
        setError('');
        await axios.post(`${API_BASE_URL}/user`, {
          name: formData.fullName,
          email: formData.email,
          country: formData.country,
          cpf: formData.cpf,
          rg: formData.rg,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setShowPayment(true);
      } catch (requestError) {
        const validationErrors = requestError.response?.data?.errors;
        const firstError = validationErrors ? Object.values(validationErrors).flat()[0] : null;
        setError(firstError || requestError.response?.data?.message || 'Não foi possível salvar seus dados.');
      } finally {
        setSavingProfile(false);
      }
    } else {
      setStep(prevStep => prevStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (showPayment) {
      setShowPayment(false);
    } else {
      setStep(prevStep => prevStep - 1);
    }
  };

  const handlePaymentMethodChange = async (nextMethod) => {
    if (nextMethod === paymentMethod || switchingPaymentMethod) return;

    // Troca a interface imediatamente e mantém o Brick montado depois da
    // primeira abertura. O SDK do Mercado Pago pode concluir a renderização
    // alguns instantes depois do clique e ainda precisa encontrar o contêiner.
    setPaymentMethod(nextMethod);
    const shouldMountCard = nextMethod === 'credit_card' && !cardFormMounted;

    try {
      setSwitchingPaymentMethod(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/payments/cancel-pending`, {
        curso_id: curso.id,
        next_method: nextMethod,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.already_paid) {
        handlePaymentSuccess(response.data);
        return;
      }
      if (shouldMountCard) {
        setCardFormMounted(true);
      }
    } catch (_) {
      // A criação da próxima cobrança também substitui qualquer tentativa pendente.
      if (shouldMountCard) {
        setCardFormMounted(true);
      }
    } finally {
      setSwitchingPaymentMethod(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    // Limpar erro quando usuário começar a digitar
    if (error) {
      setError('');
    }
  };

  const handleOfferSelection = () => {
    setError('');
    const params = new URLSearchParams(location.search);
    params.delete('includes_resume');
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    setStep(2);
  };
  

  return (
    <div className="carrinho-page">
      {/* Hero Header */}
      <div className="carrinho-hero">
        <div className="hero-content">
          <h1>{purchaseConfirmation ? 'Compra confirmada' : 'Carrinho de Compras'}</h1>
          <p className="hero-subtitle">
            {purchaseConfirmation
              ? 'Seu pagamento foi aprovado com sucesso'
              : 'Finalize sua compra e garanta seu Certificado'}
          </p>
        </div>
      </div>

      <div className="carrinho-container">
        {error && !purchaseConfirmation && <div className="error-message">{error}</div>}
        
        {!showPayment && !purchaseConfirmation && (
          <div className="steps">
            <div className={`step ${step === 1 ? 'active' : ''}`}>1. Adicionar curso</div>
            <div className={`step ${step === 2 ? 'active' : ''}`}>2. Revisar dados</div>
            <div className={`step ${step === 3 ? 'active' : ''}`}>3. Pagamento</div>
          </div>
        )}

        <div className={`step-content ${
          showPayment && !purchaseConfirmation
            ? 'payment-step'
            : (!showPayment && !purchaseConfirmation && step === 1 ? 'cart-offer-step' : '')
        }`}>
          {curso?.title && (
            <section className="cart-course-context" aria-label="Curso do certificado">
              <FontAwesomeIcon icon={faGraduationCap} aria-hidden="true" />
              <div>
                <span>Certificado referente ao curso</span>
                <strong>{curso.title}</strong>
              </div>
            </section>
          )}
        {purchaseConfirmation ? (
          <section
            className="purchase-confirmation"
            role="status"
            aria-live="polite"
            aria-labelledby="purchase-confirmation-title"
          >
            <div className="purchase-confirmation-icon" aria-hidden="true">✓</div>
            <span className="purchase-confirmation-badge">Compra confirmada</span>
            <h2 id="purchase-confirmation-title">Pagamento aprovado!</h2>
            <p className="purchase-confirmation-lead">
              Recebemos seu pagamento e sua compra foi concluída com segurança.
            </p>

            <div className="purchase-confirmation-access">
              <strong>Seu acesso já está liberado:</strong>
              <span>✓ Certificado digital verificável</span>
              {purchaseConfirmation.resumePurchased && (
                <span>✓ Currículo Profissional com o curso incluído</span>
              )}
            </div>

            <p className="purchase-confirmation-next">
              {purchaseConfirmation.resumePurchased
                ? 'Agora vamos direcionar você para criar e personalizar seu currículo.'
                : 'Agora vamos direcionar você para acessar seu Certificado.'}
            </p>

            <button
              type="button"
              className="purchase-confirmation-button"
              onClick={continueAfterPurchase}
            >
              {purchaseConfirmation.resumePurchased
                ? 'Criar meu currículo agora →'
                : 'Ver meu Certificado →'}
            </button>

            <p className="purchase-confirmation-redirect">
              Redirecionamento automático em alguns segundos…
            </p>
            <div className="purchase-confirmation-progress" aria-hidden="true">
              <span />
            </div>
          </section>
        ) : showPayment ? (
          <div className="payment-container">
            <button className="back-button" onClick={handlePrevStep}>
              ← Voltar aos dados
            </button>
            <OrderSummary
              certificatePrice={certificatePrice}
              includesResume={includesResume}
            />
            {paymentConfig.fourth_certificate_discount && (
              <div className="fourth-certificate-discount" role="status">
                <strong>Seu benefício de fidelidade foi aplicado: 50% OFF</strong>
                <span>
                  Este é o seu quarto certificado. O desconto vale para qualquer curso e não expira.
                </span>
              </div>
            )}
            <div className="payment-method-tabs" role="tablist" aria-label="Forma de pagamento">
              <button
                type="button"
                role="tab"
                aria-selected={paymentMethod === 'pix'}
                className={paymentMethod === 'pix' ? 'active' : ''}
                onClick={() => handlePaymentMethodChange('pix')}
                disabled={switchingPaymentMethod}
              >
                <FontAwesomeIcon icon={faQrcode} /> PIX
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={paymentMethod === 'credit_card'}
                className={paymentMethod === 'credit_card' ? 'active' : ''}
                onClick={() => handlePaymentMethodChange('credit_card')}
                disabled={!paymentConfig.card_enabled || switchingPaymentMethod}
              >
                <FontAwesomeIcon icon={faCreditCard} /> Cartão de crédito
              </button>
            </div>

            {paymentMethod === 'pix' && (
              <PixPayment
                curso={{ ...curso, price: totalPrice }}
                includesResume={includesResume}
                onPaymentSuccess={handlePaymentSuccess}
              />
            )}
            {paymentMethod === 'credit_card' && !cardFormMounted && (
              <div className="card-payment-preparing" role="status">
                Preparando formulário seguro…
              </div>
            )}
            {cardFormMounted && (
              <div
                className="card-payment-panel"
                data-testid="card-payment-panel"
                hidden={paymentMethod !== 'credit_card'}
                aria-hidden={paymentMethod !== 'credit_card'}
              >
                <CardPayment
                  curso={{ ...curso, price: totalPrice }}
                  userData={{ email: formData.email, cpf: formData.cpf }}
                  publicKey={paymentConfig.mercado_pago_public_key}
                  includesResume={includesResume}
                  maxInstallments={paymentConfig.max_installments}
                  onPaymentSuccess={handlePaymentSuccess}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            {step === 1 && (
              !curso?.id || hasResumeAccess === null ? (
                <div className="cart-offer-loading" role="status">Carregando oferta disponível...</div>
              ) : (
                <CertificateOffer
                  courseId={curso.id}
                  courseName={curso.title}
                  pricingOverride={{
                    price: certificatePrice,
                    cardEnabled: paymentConfig.card_enabled,
                  }}
                  resumeAccessOverride={hasResumeAccess}
                  onSelectOffer={handleOfferSelection}
                />
              )
            )}

            {step === 2 && (
              <>
                <h2>Revisar dados</h2>
                <div className="form-container">
                  <div className="form-row">
                    <div className="form-group">
                      <label>País *</label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                        placeholder="Brasil"
                      />
                    </div>
                    <div className="form-group">
                      <label>Nome completo *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        placeholder="Seu nome completo"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        RG *
                        {lockedFields.rg && <span className="field-lock-badge">🔒 cadastrado</span>}
                      </label>
                      <input
                        type="text"
                        name="rg"
                        value={formData.rg}
                        onChange={handleInputChange}
                        placeholder="Seu RG"
                        required
                        readOnly={lockedFields.rg}
                        className={lockedFields.rg ? 'input-locked' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        CPF *
                        {lockedFields.cpf && <span className="field-lock-badge">🔒 cadastrado</span>}
                      </label>
                      <input
                        type="text"
                        name="cpf"
                        value={formData.cpf}
                        onChange={handleInputChange}
                        required
                        placeholder="000.000.000-00"
                        readOnly={lockedFields.cpf}
                        className={lockedFields.cpf ? 'input-locked' : ''}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Email *
                        {lockedFields.email && <span className="field-lock-badge">🔒 cadastrado</span>}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="seu@email.com"
                        readOnly={lockedFields.email}
                        className={lockedFields.email ? 'input-locked' : ''}
                      />
                    </div>
                  </div>

                  <div className="info-box">
                    <p><strong>Importante:</strong> Os dados informados serão utilizados para emissão do Certificado.</p>
                  </div>
                </div>
                
                <div className="navigation-buttons">
                  <button className="prev-button" onClick={handlePrevStep}>
                    ← Voltar
                  </button>
                  <button className="next-button" onClick={handleNextStep} disabled={savingProfile}>
                    {savingProfile ? 'Salvando dados...' : 'Ir para Pagamento →'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default Carrinho;
